import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import validator from 'validator';
import { login, loginVerifyCode } from "../../../services/AuthService";
import Utils from "../../../utils";
import { CLIENT_LOCAL_STORE, SALT } from "../../../configs/AppConfig";
import { encryptData } from "../../../utils/crypto";
import { Lang } from "../../../lang";
import Logo from '../../../assets/Images/Docusafe_logo.webp';
import './Auth.css';

// ✅ CustomInput import
import CustomInput from "../../../components/CustomInput";

function Login() {
    document.title = Lang.login_title;
    document.getElementsByTagName("META")[4].content = Lang.login_meta_desc;
    document.getElementsByTagName("META")[5].content = Lang.login_meta_keyword;

    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [isVerify, setIsVerify] = useState(false);
    const [userData, setUserData] = useState({});
    const [code, setCode] = useState('');
    const [passwordVisible, setPasswordVisible] = useState(false);

    const errorsObj = { email: '', password: '', code: '' };
    const [errors, setErrors] = useState(errorsObj);

    // ✔ Hide error on typing
    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        setErrors(prev => ({ ...prev, email: '' }));
    };
    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
        setErrors(prev => ({ ...prev, password: '' }));
    };
    const handleCodeChange = (e) => {
        setCode(e.target.value);
        setErrors(prev => ({ ...prev, code: '' }));
    };

    function onLogin(e) {
        e.preventDefault();
        let error = false;
        const errorObj = { ...errorsObj };

        if (!email) {
            errorObj.email = 'Email is required';
            error = true;
        } else if (!validator.isEmail(email)) {
            errorObj.email = 'Enter a valid email';
            error = true;
        }

        if (!password) {
            errorObj.password = 'Password is required';
            error = true;
        }

        setErrors(errorObj);
        if (error) return;

        setLoading(true);

        login({ email, password })
            .then(response => {
                if (response.data.data.qr_code) {
                    setIsVerify(true);
                    setUserData(response.data.data);
                } else {
                    const encryptedData = encryptData(response.data.data, SALT);
                    localStorage.setItem(CLIENT_LOCAL_STORE, encryptedData);
                    Utils.updateLastActivity();
                    navigate('/client-portal');
                }
            })
            .catch(err => toast.error(Utils.getErrorMessage(err)))
            .finally(() => setLoading(false));
    }

    const onVerify = (e) => {
        e.preventDefault();

        let errorObj = { ...errorsObj };
        let error = false;

        if (!code) {
            errorObj.code = 'Code is required';
            error = true;
        }

        setErrors(errorObj);
        if (error) return;

        setLoading(true);

        loginVerifyCode({
            id: userData.id,
            company_login_id: userData.company_user_id,
            code
        })
            .then(() => {
                const encryptedData = encryptData(userData, SALT);
                localStorage.setItem(CLIENT_LOCAL_STORE, encryptedData);
                Utils.updateLastActivity();
                navigate('/client-portal');
            })
            .catch(err => toast.error(Utils.getErrorMessage(err)))
            .finally(() => setLoading(false));
    };

    return (
        <div className="login-container p-0">
            <div className="bg-elements">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <div className="floating-shape shape-3"></div>
            </div>

            <div className="login-content">

                {/* LEFT Branding Section */}
                <div className="login-branding">
                    <div className="brand-content">
                        <div className="brand-text">
                            <h2>Welcome to <br />Contract Portal</h2>
                            <p className="my-3">
                                Streamline your entire contract lifecycle with <br />
                                secure, smart, and collaborative tools.
                            </p>
                        </div>

                        <div className="feature-highlights">
                            <div className="feature-item">
                                <div className="icon-box"><i className="bi bi-shield-check"></i></div>
                                <span>Smart Negotiation Contracts</span>
                            </div>
                            <div className="feature-item">
                                <div className="icon-box"><i className="bi bi-check2-circle"></i></div>
                                <span>Real-time Collaboration</span>
                            </div>
                            <div className="feature-item">
                                <div className="icon-box"><i className="bi bi-lock"></i></div>
                                <span>Secure Cloud Document Storage</span>
                            </div>
                            <div className="feature-item">
                                <div className="icon-box"><i className="bi bi-file-earmark-text"></i></div>
                                <span>Legally Binding E-Signatures</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* RIGHT Login Section */}
                <div className="login-form-section">
                    <div className="auth-card">

                        <div className="card-header-auth">
                            <h2>Welcome Back</h2>
                            <p className="fw-semibold mb-3">Sign in to your secure portal</p>
                        </div>

                        {/* === VERIFICATION STEP === */}
                        {isVerify ? (
                            <form onSubmit={onVerify} className="auth-form">
                                <div className="form-group">
                                    <CustomInput
                                        label="Authenticator Code"
                                        name="code"
                                        type="number"
                                        value={code}
                                        onChange={handleCodeChange}
                                        error={errors.code}
                                        placeholder="Enter 6-digit code"
                                    />
                                </div>

                                <button type="submit" className="submit-btn" disabled={loading}>
                                    <span>Verify & Continue</span>
                                    <i className="bi bi-arrow-right"></i>
                                </button>

                                <button
                                    type="button"
                                    className="back-btn"
                                    onClick={() => setIsVerify(false)}
                                >
                                    <i className="bi bi-arrow-left"></i> Back to Login
                                </button>
                            </form>
                        ) : (
                            /* === LOGIN STEP === */
                            <form onSubmit={onLogin} className="auth-form">

                                {/* EMAIL */}
                                <div className="form-group mb-3">
                                    <CustomInput
                                        label="Email Address"
                                        name="email"
                                        value={email}
                                        onChange={handleEmailChange}
                                        error={errors.email}
                                        required
                                        placeholder="Enter your email"
                                    />
                                </div>

                                {/* PASSWORD + Eye Toggle */}
                                <div className="form-group mb-3">
                                    <div className="input-wrapper password-wrapper">
                                        <CustomInput
                                            label="Password"
                                            name="password"
                                            type={passwordVisible ? "text" : "password"}
                                            value={password}
                                            onChange={handlePasswordChange}
                                            error={errors.password}
                                            required
                                            placeholder="Enter your password"
                                        />

                                        <button
                                            type="button"
                                            className="password-toggle"
                                            onClick={() => setPasswordVisible(!passwordVisible)}
                                        >
                                            <i className={`bi ${passwordVisible ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                                        </button>
                                    </div>

                                    <div className="text-end mt-2">
                                        <NavLink to="/forgot-password" className="forgot-password-link text-decoration-none">
                                            Forgot Password?
                                        </NavLink>
                                    </div>
                                </div>

                                {/* SUBMIT BUTTON */}
                                <button type="submit" className="submit-btn" disabled={loading}>
                                    <span>Sign In to Portal</span>
                                    <i className="bi bi-arrow-right"></i>
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Login;
